import React, { useState, useMemo, useEffect, useRef } from 'react';
import './FieldTypePicker.css';

/*
 * FieldTypePicker – full-screen modal for choosing a field type.
 *
 * Props
 * ─────
 *   open       : boolean
 *   fieldTypes : Array<{ value, label, group }>
 *   sampleValues : { [value]: string }
 *   currentValue : string   – currently selected type
 *   onSelect   : (value: string) => void
 *   onClose    : () => void
 */

export default function FieldTypePicker({ open, fieldTypes, sampleValues = {}, currentValue, onSelect, onClose }) {
  const [search, setSearch] = useState('');
  const [activeGroup, setActiveGroup] = useState('All');
  const searchRef = useRef(null);

  // Reset search when opened
  useEffect(() => {
    if (open) {
      setSearch('');
      setActiveGroup('All');
      setTimeout(() => searchRef.current?.focus(), 80);
    }
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Build groups with counts
  const groups = useMemo(() => {
    const map = new Map();
    fieldTypes.forEach(ft => {
      if (!map.has(ft.group)) map.set(ft.group, []);
      map.get(ft.group).push(ft);
    });
    return Array.from(map.entries()).map(([name, items]) => ({ name, items }));
  }, [fieldTypes]);

  // Filter by search + active group
  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return fieldTypes.filter(ft => {
      if (activeGroup !== 'All' && ft.group !== activeGroup) return false;
      if (q && !ft.label.toLowerCase().includes(q) && !ft.value.toLowerCase().includes(q) && !ft.group.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [fieldTypes, search, activeGroup]);

  // Group filtered items
  const filteredGroups = useMemo(() => {
    const map = new Map();
    filtered.forEach(ft => {
      if (!map.has(ft.group)) map.set(ft.group, []);
      map.get(ft.group).push(ft);
    });
    return Array.from(map.entries()).map(([name, items]) => ({ name, items }));
  }, [filtered]);

  // Count per group (respecting search)
  const groupCounts = useMemo(() => {
    const q = search.toLowerCase().trim();
    const counts = { All: 0 };
    fieldTypes.forEach(ft => {
      if (q && !ft.label.toLowerCase().includes(q) && !ft.value.toLowerCase().includes(q) && !ft.group.toLowerCase().includes(q)) return;
      counts.All = (counts.All || 0) + 1;
      counts[ft.group] = (counts[ft.group] || 0) + 1;
    });
    return counts;
  }, [fieldTypes, search]);

  if (!open) return null;

  return (
    <div className="ftp-overlay" onClick={onClose}>
      <div className="ftp-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="ftp-header">
          <h3 className="ftp-title">Choose a Type</h3>
          <div className="ftp-search-box">
            <svg className="ftp-search-icon" width="15" height="15" viewBox="0 0 15 15" fill="none">
              <circle cx="6.5" cy="6.5" r="5" stroke="currentColor" strokeWidth="1.3"/>
              <path d="M10.5 10.5L13.5 13.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
            </svg>
            <input
              ref={searchRef}
              type="text"
              className="ftp-search-input"
              placeholder="Find Type..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button className="ftp-close" onClick={onClose}>×</button>
        </div>

        <div className="ftp-body">
          {/* Sidebar categories */}
          <div className="ftp-sidebar">
            <div
              className={`ftp-cat ${activeGroup === 'All' ? 'active' : ''}`}
              onClick={() => setActiveGroup('All')}
            >
              <span className="ftp-cat-name">All</span>
              <span className="ftp-cat-count">{groupCounts.All || 0}</span>
            </div>
            {groups.map(g => (
              <div
                key={g.name}
                className={`ftp-cat ${activeGroup === g.name ? 'active' : ''} ${!groupCounts[g.name] ? 'ftp-cat-dim' : ''}`}
                onClick={() => setActiveGroup(g.name)}
              >
                <span className="ftp-cat-name">{g.name}</span>
                <span className="ftp-cat-count">{groupCounts[g.name] || 0}</span>
              </div>
            ))}
          </div>

          {/* Type cards grid */}
          <div className="ftp-grid-area">
            {filtered.length === 0 ? (
              <div className="ftp-empty">No types match "{search}"</div>
            ) : (
              filteredGroups.map(g => (
                <div key={g.name} className="ftp-group-section">
                  {(activeGroup === 'All' || filteredGroups.length > 1) && (
                    <div className="ftp-group-label">{g.name}</div>
                  )}
                  <div className="ftp-grid">
                    {g.items.map(ft => {
                      const samples = sampleValues[ft.value];
                      const sampleList = samples
                        ? samples.split(/[,;]/).map(s => s.trim()).filter(Boolean).slice(0, 3)
                        : [];
                      return (
                        <div
                          key={ft.value}
                          className={`ftp-card ${currentValue === ft.value ? 'ftp-card-selected' : ''}`}
                          onClick={() => { onSelect(ft.value); onClose(); }}
                        >
                          <div className="ftp-card-title">{ft.label}</div>
                          {sampleList.length > 0 ? (
                            <div className="ftp-card-samples">
                              {sampleList.map((s, i) => (
                                <div key={i} className="ftp-card-sample">{s}</div>
                              ))}
                            </div>
                          ) : (
                            <div className="ftp-card-desc">{ft.value}</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
